import CustomError from '@screens/CustomError';
import { prismicService } from '@services/businessContainer';
import { GetServerSideProps } from 'next';
import { LFPage, injectLayoutData } from '@utils/layout';
import { tryit } from 'radash';
import LayoutMultiStepForm from '@components/LayoutMultiStepForm';
import {
  FirstOrderFormProvider,
  useFirstOrderFormContext,
} from '@context/firstOrderForm';
import { MultiStepFormDocument } from '@monofront/types';
import { injectGetServerSidePropsHeaders } from '@utils/query';
import { JSXElementConstructor } from 'react';
import MultiStepForm from '@screens/MultiStepForm';
import { validatePrismicContent } from '@monofront/business/hooks';
import { MultiStepFormStep } from '@monofront/business/stateMachines';

interface IFirstOrderFormPageProps {
  pageContents?: {
    steps: Record<string, MultiStepFormStep>;
  };
}

const FirstOrderFormPage: LFPage<IFirstOrderFormPageProps> = ({
  pageContents,
}) => {
  const {
    form,
    send,
    state,
    steps,
    isValidating,
    setIsValidating,
    currentStep,
  } = useFirstOrderFormContext();

  if (!pageContents) {
    return <CustomError statusCode={404} />;
  }

  return (
    <MultiStepForm
      state={state}
      send={send}
      form={form}
      steps={steps}
      isValidating={isValidating}
      setIsValidating={setIsValidating}
      currentStep={currentStep}
    />
  );
};

FirstOrderFormPage.getLayout =
  (props: IFirstOrderFormPageProps) =>
  ({
    children,
  }: {
    children:
      | React.ReactElement<any, string | JSXElementConstructor<any>>
      | undefined;
  }) => {
    return (
      <FirstOrderFormProvider content={props.pageContents}>
        <LayoutMultiStepForm>{children}</LayoutMultiStepForm>
      </FirstOrderFormProvider>
    );
  };

export const getServerSideProps =
  injectGetServerSidePropsHeaders<IFirstOrderFormPageProps>(
    injectLayoutData<
      IFirstOrderFormPageProps,
      GetServerSideProps<IFirstOrderFormPageProps>
    >(async ({ locale, previewData }) => {
      const tryGetDocumentByUid = tryit<() => Promise<MultiStepFormDocument>>(
        async () =>
          await prismicService.getDocumentByUid<MultiStepFormDocument>(
            'multi_step_form',
            'first-order-form-desktop',
            previewData,
            {
              fetchLinks: [
                'multi_step_form_slice.header_type',
                'multi_step_form_slice.is_header_bottom_border_curved',
                'multi_step_form_slice.body',
              ],
              ...(locale ? { lang: `${locale}-${locale}` } : {}),
            },
          ),
      );

      const [err, document] = await tryGetDocumentByUid();

      if (err || !document) {
        return {
          notFound: true,
        };
      }

      const formattedPageContents = validatePrismicContent(document);

      return {
        props: {
          pageContents: formattedPageContents,
        },
      };
    }),
  );

export default FirstOrderFormPage;
